import { Button, Typography } from "@material-ui/core"
import { postServiceCenterContact, SingleServiceCenter } from "api"
import { trackPopUpClicks, trackSuccessLead } from "common/analytics"
import FormikForm from "common/bit/form/FormikForm"
import { format } from "date-fns"
import { useState } from "react"
import FormInput from "reusable/formsComp/FormInput"
import FormSelect from "reusable/formsComp/FormSelect"
import styled from "styled-components"
import DateTimeInput from "../../../../reusable/formsComp/DateTimeInput"
import { FormStatus } from "../popups/ContentUsPopup"
import {
  generalMaxMinTime,
  initialFormState,
  PROBLEM_DESCRIPTION_MAX_LENGTH,
  schema,
  TreatmentMaxMinTime,
  treatmentTypes,
} from "./TreatmentSchema"

const DATE_FORMAT = "dd/MM/yyyy"
const TIME_FORMAT = "HH:mm"

const StyledFormDiv = styled.div<{ $hide: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  width: 100%;
  .MuiSelect-nativeInput {
    opacity: ${(p) => (p.$hide ? 0 : 1)};
    border: none;
    height: 100%;
    ::placeholder {
      color: rgba(16, 16, 16, 1);
      font-size: 18px;
      padding-right: 10px;
      font-family: MazdaType, OENaPro, sans-serif;
    }
  }
  label {
    font-family: MazdaType, OENaPro, sans-serif;
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding-left: 20px;
    padding-right: 20px;
  }
`

const StyledCol = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StyledRowCol = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: auto;
  gap: 2px;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
    column-gap: 16px;
  }
`
const StyledWrap = styled.div`
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    height: 81.6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`
const StyledButton = styled(Button)`
  margin-top: 10px;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 4px;
    height: 41px;
  }
`
const StyledRequiredFields = styled(Typography).attrs({ variant: "body2" })`
  color: ${(p) => p.theme.colors.mazdaRed};
  margin-left: auto;
`
const StyledRecallComment = styled(Typography).attrs({ variant: "body2" })`
  /* margin-left: auto; */
`

type Props = {
  centerData: SingleServiceCenter
  setFormStatus: any
}
const TreatmentForm = ({ centerData, setFormStatus }: Props) => {
  const [treatmentTimeMinMax, setTreatmentTimeMinMax] =
    useState<TreatmentMaxMinTime>()

  // Check if service center works on Friday (ו)
  // const isWorkingFriday = Object.values(centerData.schedule || {}).some(
  //   (days) => days.includes("ו")
  // )

  return (
    <FormikForm
      validationSchema={schema}
      initialValues={initialFormState}
      onSubmit={async (values) => {
        try {
          const { data } = await postServiceCenterContact({
            name: values.fullName,
            mobilePhone: values.phoneNumber,
            serviceCenterId: centerData.id,
            applicationType: "Treatment",
            rishui: values.licenseNumber,
            email: centerData.email,
            customerEmail: null,
            content: values.problemDescription,
            treatmentDate: format(values.treatmentDate, DATE_FORMAT),
            treatmentTime: format(values.treatmentDate, TIME_FORMAT),
            treatmentType: values.treatmentType,
          })
          if (data.body) {
            trackSuccessLead("קביעת ביקור", "service_center_lead_success")
            setFormStatus(FormStatus.Success)
          } else {
            setFormStatus(FormStatus.Error)
          }
        } catch (error) {
          console.error(error)
          setFormStatus(FormStatus.Error)
        }
      }}
      render={(formik) => (
        <StyledFormDiv $hide={formik.values.treatmentType}>
          <StyledRecallComment>
            לביצוע קריאה חוזרת (ריקול) יש לתאם טלפונית עם מרכז השירות.
          </StyledRecallComment>
          <StyledRequiredFields>* שדות חובה</StyledRequiredFields>

          <StyledRowCol>
            <StyledCol>
              <FormInput
                name="fullName"
                label="שם מלא"
                required
                onFocusCapture={() =>
                  trackPopUpClicks("קביעת ביקור", "fullName")
                }
              />
              <FormSelect
                name="treatmentType"
                required
                label="סוג טיפול"
                values={treatmentTypes}
                map={(item) => ({ id: item.id, name: item.name })}
                onClick={() => trackPopUpClicks("קביעת ביקור", "treatmentType")}
                onChange={(event) => {
                  formik.setFieldValue("treatmentType", event.target.value)
                  const treatmentType = treatmentTypes.find(
                    (type) => type.id === event.target.value
                  )
                  if (!centerData.isMainGarage) {
                    setTreatmentTimeMinMax(generalMaxMinTime)
                  } else if (treatmentType) {
                    setTreatmentTimeMinMax(treatmentType.minMaxTime)
                  }
                }}
              />
              <FormInput
                name="licenseNumber"
                label="מספר לוחית רישוי"
                required
                onFocusCapture={() =>
                  trackPopUpClicks("קביעת ביקור", "licenseNumber")
                }
              />
            </StyledCol>
            <StyledCol>
              <FormInput
                name="phoneNumber"
                label="טלפון נייד"
                required
                type="tel"
                onFocusCapture={() =>
                  trackPopUpClicks("קביעת ביקור", "phoneNumber")
                }
              />

              <DateTimeInput
                name="treatmentDate"
                label="בחירת מועד הגעה"
                minTime={treatmentTimeMinMax?.minTime}
                maxTime={treatmentTimeMinMax?.maxTime}
                disable={!treatmentTimeMinMax}
              />
              {formik.values.treatmentType === "Problem" && (
                <FormInput
                  name="problemDescription"
                  label="פירוט התקלה"
                  required
                  multiline
                  onFocusCapture={() =>
                    trackPopUpClicks("פירוט התקלה", "problemDescription")
                  }
                  maxLength={PROBLEM_DESCRIPTION_MAX_LENGTH}
                />
              )}
              <StyledWrap>
                <StyledButton
                  disabled={formik.isSubmitting}
                  fullWidth
                  variant="text"
                  type={"submit"}
                >
                  שליחה
                </StyledButton>
              </StyledWrap>
            </StyledCol>
          </StyledRowCol>
        </StyledFormDiv>
      )}
    />
  )
}

export default TreatmentForm
